import DescriptionIcon from '@mui/icons-material/Description';
import ForumIcon from '@mui/icons-material/Forum';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import {
    Box,
    MenuItem,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import { usePostHog } from '@posthog/react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { TrackedSelect, TrackedTextField } from 'src/theme/StyledFields';
import { capture } from "../../analytics/posthog";
import { useLessonPlanEditor } from '../../context/LessonPlanEditorContext';
import { SessionSlideModel } from '../../object-actions/types/types';
import MarkdownEditor from '../MarkdownEditor';
import YouTubeCTA from '../youtube/YouTubeCTA';
import CoachingTip from './CoachingTip';
import PriorWeekCheckInBubble from './PriorWeekCheckInBubble';
import ScriptsDisplay from './ScriptsDisplay';

// Helper type for display format (with scripts object)
type SlideDisplayFormat = Omit<SessionSlideModel, 'script_open' | 'script_on_neutral' | 'script_on_negative' | 'script_on_positive'> & {
    scripts: {
        open: string;
        on_neutral: string;
        on_negative: string;
        on_positive: string;
    };
};

type LessonPlanRole = 'payer' | 'athlete' | 'coach' | 'parent';

interface LessonPlanSlideProps {
    slide: SlideDisplayFormat;
    slideNumber: number;
    totalSlides: number;
    myRoles?: LessonPlanRole[];
    showPriorWeekCheckIn?: boolean;
}

const DEFAULT_SCRIPTS = {
    open: '',
    on_neutral: '',
    on_negative: '',
    on_positive: ''
};

const LessonPlanSlide: React.FC<LessonPlanSlideProps> = ({
    slide,
    slideNumber,
    totalSlides,
    myRoles,
    showPriorWeekCheckIn = false,
}) => {
    const theme = useTheme();
    const posthog = usePostHog();
    const { isEditMode, updateSlide, getSlideWithEdits } = useLessonPlanEditor();

    // Normalize scripts: handle undefined or malformed slide.scripts
    const safeScripts = slide.scripts && typeof slide.scripts === 'object'
        ? {
            open: slide.scripts.open ?? '',
            on_neutral: slide.scripts.on_neutral ?? '',
            on_negative: slide.scripts.on_negative ?? '',
            on_positive: slide.scripts.on_positive ?? ''
        }
        : DEFAULT_SCRIPTS;
    if (!slide.scripts || typeof slide.scripts !== 'object') {
        console.warn('Slide missing scripts object. Using defaults.', { slide_id: slide.id, slide_number: slideNumber });
        capture(posthog, 'missing_slide_scripts', { slide_id: slide.id, slide_number: slideNumber, total_slides: totalSlides });
    }

    // Get the original slide (without scripts object) for merging
    const originalSlide: SessionSlideModel = {
        ...slide,
        script_open: safeScripts.open,
        script_on_neutral: safeScripts.on_neutral,
        script_on_negative: safeScripts.on_negative,
        script_on_positive: safeScripts.on_positive
    };

    // Get merged slide data (original + edits)
    const mergedSlide = getSlideWithEdits(originalSlide);

    // Log when coaching_tip is missing
    if (mergedSlide.coaching_tip === undefined || mergedSlide.coaching_tip === null || mergedSlide.coaching_tip === "") {
        console.warn('Slide missing coaching_tip.', { slide_id: slide.id, slide_number: slideNumber });
        capture(posthog, 'missing_slide_coaching_tip', { slide_id: slide.id, slide_number: slideNumber, total_slides: totalSlides });
    }

    // Convert back to display format with scripts object
    const slideData = {
        ...mergedSlide,
        scripts: {
            open: mergedSlide.script_open ?? safeScripts.open,
            on_neutral: mergedSlide.script_on_neutral ?? safeScripts.on_neutral,
            on_negative: mergedSlide.script_on_negative ?? safeScripts.on_negative,
            on_positive: mergedSlide.script_on_positive ?? safeScripts.on_positive
        }
    };

    const slideTypeConfig = {
        activity: {
            label: 'Activity',
            color: 'primary' as const,
            icon: <TrackChangesIcon sx={{ fontSize: '1.5rem' }} />,
            bgColor: theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.1)'
                : 'rgba(25, 118, 210, 0.08)',
            borderColor: theme.palette.primary.main
        },
        worksheet: {
            label: 'Worksheet',
            color: 'secondary' as const,
            icon: <DescriptionIcon sx={{ fontSize: '1.5rem' }} />,
            bgColor: theme.palette.mode === 'dark'
                ? 'rgba(156, 39, 176, 0.1)'
                : 'rgba(156, 39, 176, 0.08)',
            borderColor: theme.palette.secondary.main
        },
        conversation: {
            label: 'Conversation',
            color: 'success' as const,
            icon: <ForumIcon sx={{ fontSize: '1.5rem' }} />,
            bgColor: theme.palette.mode === 'dark'
                ? 'rgba(46, 125, 50, 0.1)'
                : 'rgba(46, 125, 50, 0.08)',
            borderColor: theme.palette.success.main
        }
    };

    let config = slideTypeConfig[slideData.type];
    if (!config) {
        config = slideTypeConfig['activity'];
        console.warn(`Unknown slide type: ${slideData.type}. Using default config.`);
        capture(posthog, "unknown_slide_type", { slide_type: slideData.type, slide_number: slideNumber, slide_id: slide.id });
    }

    return (
        <>
            {showPriorWeekCheckIn && (
                <PriorWeekCheckInBubble color={config.bgColor} borderColor={config.borderColor} />
            )}
        <Paper
            elevation={2}
            data-testid="lesson-plan-slide"
            sx={{
                p: 3,
                mb: 3,
                backgroundColor: config?.bgColor,
                borderLeft: `4px solid ${config.borderColor}`,
                borderRadius: "0 0 2px 2px"
            }}
        >
            {/* Slide Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    {isEditMode ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {config.icon}
                                <TrackedTextField
                                    fullWidth
                                    label="Title"
                                    value={slideData.title || ''}
                                    onChange={(e) => updateSlide(slide.id, { title: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    data-attr="lesson-plan-slide_field-title"
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                                >
                                    {slideNumber} of {totalSlides}
                                </Typography>
                            </Box>
                            <TrackedSelect
                                value={slideData.type}
                                onChange={(e) => updateSlide(slide.id, { type: e.target.value as 'activity' | 'worksheet' | 'conversation' })}
                                size="small"
                                sx={{ minWidth: 150 }}
                                data-attr="lesson-plan-slide_field-type"
                            >
                                <MenuItem value="activity">Activity</MenuItem>
                                <MenuItem value="worksheet">Worksheet</MenuItem>
                                <MenuItem value="conversation">Conversation</MenuItem>
                            </TrackedSelect>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            {config.icon}
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'text.primary',
                                    flex: 1, flexGrow: 1
                                }}
                            >
                                {slideData.title} <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    {slideNumber} of {totalSlides}
                                </Typography>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Slide Body */}
            {isEditMode ? (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Body
                    </Typography>
                    <MarkdownEditor
                        value={slideData.body || ''}
                        onChange={(value) => updateSlide(slide.id, { body: value })}
                        placeholder="Enter slide content here..."
                        minHeight={300}
                        data-attr="lesson-plan-slide_field-body"
                    />
                </Box>
            ) : (
                slideData.body && (
                    <Box
                        sx={{
                            mt: 2,
                            mb: 2,
                            '& p': { margin: 0 },
                            '& p + p': { marginTop: 2 },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                marginTop: 2,
                                marginBottom: 1,
                                fontWeight: 'bold',
                                color: 'text.primary'
                            },
                            '& ul, & ol': {
                                marginTop: 1,
                                marginBottom: 1,
                                paddingLeft: 3
                            },
                            '& li': {
                                marginBottom: 0.5
                            },
                            '& a': {
                                color: theme.palette.primary.main,
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            },
                            '& blockquote': {
                                borderLeft: `4px solid ${theme.palette.divider}`,
                                paddingLeft: 2,
                                marginLeft: 0,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)',
                                padding: 1.5,
                                borderRadius: 1,
                                marginTop: 2,
                                marginBottom: 2
                            },
                            '& code': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.1)'
                                    : 'rgba(0, 0, 0, 0.05)',
                                padding: '2px 6px',
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '0.9em'
                            },
                            '& pre': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)',
                                padding: 2,
                                borderRadius: 1,
                                overflow: 'auto',
                                marginTop: 2,
                                marginBottom: 2
                            },
                            color: 'text.primary',
                            lineHeight: 1.7
                        }}
                    >
                        <ReactMarkdown>{slideData.body}</ReactMarkdown>
                    </Box>
                )
            )}

            {/* YouTube video discovery: editable query in edit mode, CTA otherwise */}
            {isEditMode ? (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        YouTube Search Query
                    </Typography>
                    <TrackedTextField
                        fullWidth
                        value={slideData.media_search_query || ''}
                        onChange={(e) => updateSlide(slide.id, { media_search_query: e.target.value })}
                        placeholder="e.g. youth athlete confidence highlights (leave empty to hide the video button)"
                        variant="outlined"
                        size="small"
                        helperText="When set, learners see a button to find an inspiring video for this slide."
                        data-attr="lesson-plan-slide_field-youtube-search-query"
                    />
                </Box>
            ) : (
                <YouTubeCTA slide={slideData} />
            )}

            {/* Scripts Section */}
            {isEditMode ? (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Scripts
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TrackedTextField
                            fullWidth
                            label="Script: Open"
                            value={slideData.scripts.open || ''}
                            onChange={(e) => updateSlide(slide.id, { script_open: e.target.value })}
                            variant="outlined"
                            multiline
                            rows={3}
                            data-attr="lesson-plan-slide_field-script-open"
                        />
                        <TrackedTextField
                            fullWidth
                            label="Script: On Neutral"
                            value={slideData.scripts.on_neutral || ''}
                            onChange={(e) => updateSlide(slide.id, { script_on_neutral: e.target.value })}
                            variant="outlined"
                            multiline
                            rows={3}
                            data-attr="lesson-plan-slide_field-script-neutral"
                        />
                        <TrackedTextField
                            fullWidth
                            label="Script: On Negative"
                            value={slideData.scripts.on_negative || ''}
                            onChange={(e) => updateSlide(slide.id, { script_on_negative: e.target.value })}
                            variant="outlined"
                            multiline
                            rows={3}
                            data-attr="lesson-plan-slide_field-script-negative"
                        />
                        <TrackedTextField
                            fullWidth
                            label="Script: On Positive"
                            value={slideData.scripts.on_positive || ''}
                            onChange={(e) => updateSlide(slide.id, { script_on_positive: e.target.value })}
                            variant="outlined"
                            multiline
                            rows={3}
                            data-attr="lesson-plan-slide_field-script-positive"
                        />
                    </Box>
                </Box>
            ) : (
                <ScriptsDisplay scripts={slideData.scripts} myRoles={myRoles} />
            )}

            {/* Coaching Tip */}
            {isEditMode ? (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Coaching Tip
                    </Typography>
                    <MarkdownEditor
                        value={slideData.coaching_tip || ''}
                        onChange={(value) => updateSlide(slide.id, { coaching_tip: value })}
                        placeholder="Enter coaching tip here..."
                        minHeight={150}
                        data-attr="lesson-plan-slide_field-coaching-tip"
                    />
                </Box>
            ) : (
                slideData.coaching_tip && (myRoles?.includes('coach') || myRoles?.includes('parent')) && (
                    <Box
                        data-testid="lesson-plan-slide-coaching-tip"
                        data-attr="lesson-plan-slide-coaching-tip"
                        sx={{ mt: 2 }}
                    >
                        <CoachingTip tip={slideData.coaching_tip} />
                    </Box>
                )
            )}

        </Paper>
        </>
    );
};

export default LessonPlanSlide;
